import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

const GalleryModal = ({ idGallery, isOpen, onClose }) => {
  
  const [newFiles, setNewFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // =============================
  // FETCH EXISTING FILES
  // =============================
  useEffect(() => {
    if (isOpen && idGallery) {
      fetch(`${process.env.REACT_APP_BASE_URL}api/galleryitem/${idGallery}`)
        .then((res) => res.json())
        .then((data) => setExistingFiles(data))
        .catch((err) => console.log(err));
    }
  }, [isOpen, idGallery]);

  // =============================
  // DROPZONE
  // =============================
  const onDrop = useCallback((acceptedFiles) => {
    const mappedFiles = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    setNewFiles((prev) => [...prev, ...mappedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "video/*": [],
    },
    multiple: true,
  });

  const removeNewFile = (fileName) => {
    setNewFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const removeExistingFile = async (id) => {
    await fetch(`${process.env.REACT_APP_BASE_URL}api/galleryitem/${id}`, {
      method: "DELETE",
    });

    setExistingFiles((prev) => prev.filter((file) => file.id !== id));
  };

  // =============================
  // UPLOAD
  // =============================
  const handleUpload = async () => {
    if (newFiles.length === 0) return;

    const formData = new FormData();
    formData.append("id_gallery", idGallery);

    newFiles.forEach((file) => {
      formData.append("files[]", file);
    });

    setIsUploading(true);
    setUploadProgress(0);

    const xhr = new XMLHttpRequest();

    xhr.open(
      "POST",
      `${process.env.REACT_APP_BASE_URL}api/galleryitem/upload`
    );

    // Progress upload
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(percent);
      }
    };

    // When finished
    xhr.onload = async () => {
      if (xhr.status === 200) {
        setNewFiles([]);
        setUploadProgress(0);
        setIsUploading(false);

        // reload existing
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/galleryitem/${idGallery}`
        );
        const data = await res.json();
        setExistingFiles(data);
      } else {
        console.error("Upload gagal");
        setIsUploading(false);
      }
    };

    xhr.onerror = () => {
      console.error("Upload error");
      setIsUploading(false);
    };

    xhr.send(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
      <div className="bg-white w-full max-w-4xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto">

        <h2 className="text-xl font-bold mb-4 text-black">
          Kelola Gallery ID: {idGallery}
        </h2>

        {/* DROPZONE */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed p-8 rounded-xl text-black text-center cursor-pointer transition
          ${isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"}
        `}
        >
          <input {...getInputProps()} />
          <p>Drag & drop atau klik untuk upload</p>
        </div>
        {isUploading && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <p className="text-sm text-center mt-1">
              Uploading... {uploadProgress}%
            </p>
          </div>
        )}

        {/* NEW FILE PREVIEW */}
        {newFiles.length > 0 && (
          <>
            <h3 className="mt-6 font-semibold">File Baru</h3>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-3">
              {newFiles.map((file, index) => (
                <div key={index} className="relative border rounded-lg">
                  {file.type.startsWith("image") ? (
                    <img
                      src={file.preview}
                      className="h-32 w-full object-cover"
                      alt=""
                    />
                  ) : (
                    <video
                      src={file.preview}
                      className="h-32 w-full object-cover"
                      controls
                    />
                  )}
                  <button
                    onClick={() => removeNewFile(file.name)}
                    className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 rounded"
                  >
                    Hapus
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleUpload}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg"
            >
              Upload Sekarang
            </button>
          </>
        )}

        {/* EXISTING FILES */}
        {existingFiles.length > 0 && (
          <>
            <h3 className="mt-8 font-semibold text-black">
              File Yang Sudah Diupload
            </h3>

            <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-3">
              {existingFiles.map((file) => (
                <div key={file.id} className="relative border rounded-lg">
                  {file.filetype === "image" ? (
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}uploads/gallery/${file.filename}`}
                      className="h-32 w-full object-cover"
                      alt=""
                    />
                  ) : (
                    <video
                      src={`${process.env.REACT_APP_BASE_URL}uploads/gallery/${file.filename}`}
                      className="h-32 w-full object-cover"
                      controls
                    />
                  )}

                  <button
                    onClick={() => removeExistingFile(file.id)}
                    className="absolute top-1 right-1 bg-red-500 text-white text-xs px-2 rounded"
                  >
                    Hapus
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* CLOSE BUTTON */}
        <div className="flex justify-end mt-8">
          <button
            onClick={onClose}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg"
          >
            Tutup
          </button>
        </div>
      </div>
    </div>
  );
};

export default GalleryModal;